import React, { Component } from 'react';
import { connect } from 'react-redux';
import PubNub from 'pubnub';
import styled, { keyframes } from 'styled-components';
import {
  FiArrowLeft, FiBarChart2, FiPhone, FiClock, FiCheckCircle,
  FiActivity, FiUsers, FiUser, FiX, FiAlertTriangle, FiPlus, FiUpload,
} from 'react-icons/fi';
import {
  loadOpsState, applyOpsUpdate, doManualAssign,
  setOpsDoctorStatus, doRegisterInstantCase, doBulkGenerateInstantLinks,
} from '../../actions/licQueue';
import config from '../../config';
import visitLogo from '../../images/common/visit-logo.svg';

const OPS_CHANNEL = 'lic-instant-ops';

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg:            '#0f1117',
  surface:       '#1a1d26',
  surfaceAlt:    '#1e2230',
  navBg:         '#161921',
  border:        '#2a2d3a',
  borderLight:   '#363b4e',
  purple:        '#8852cc',
  purpleDim:     '#8852cc22',
  green:         '#22c55e',
  greenDim:      '#22c55e1a',
  yellow:        '#f59e0b',
  yellowDim:     '#f59e0b1a',
  red:           '#ef4444',
  redDim:        '#ef44441a',
  blue:          '#3b82f6',
  blueDim:       '#3b82f61a',
  textPrimary:   '#f1f5f9',
  textSecondary: '#cbd5e1',
  textMuted:     '#64748b',
};

const STATUS_COLOR = {
  AVAILABLE:   C.green,
  IN_CALL:     C.yellow,
  WRAP_UP:     C.blue,
  OFFLINE:     C.textMuted,
  WAITING:     C.yellow,
  ASSIGNED:    C.blue,
  IN_PROGRESS: C.green,
  COMPLETED:   C.textMuted,
};

const STATUS_LABEL = {
  AVAILABLE:   'Available',
  IN_CALL:     'In Call',
  WRAP_UP:     'Wrap-up',
  OFFLINE:     'Offline',
  WAITING:     'Waiting',
  ASSIGNED:    'Assigned',
  IN_PROGRESS: 'In Progress',
  COMPLETED:   'Completed',
};

// ─── Animations ───────────────────────────────────────────────────────────────
const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${C.bg};
  color: ${C.textPrimary};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
  padding: 2rem;
  padding-top: 0;
  display: flex;
  flex-direction: column;
`;

const TopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.4rem;
  height: 5.6rem;
  padding: 0 2rem;
  background: ${C.navBg};
  border-bottom: 1px solid ${C.border};
  margin: 0 -2rem 2.4rem;
  position: sticky;
  top: 0;
  z-index: 200;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
`;

const BackBtn = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  border-radius: 0.5rem;
  padding: 0.5rem 1.1rem;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    border-color: ${C.purple};
    color: ${C.purple};
    background: ${C.purpleDim};
  }
`;

const BrandArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1.4rem;
`;

const VisitLogo = styled.img`
  height: 2.4rem;
  width: auto;
  background: #fff;
  padding: 0.25rem 0.7rem;
  border-radius: 0.4rem;
`;

const LogoDivider = styled.div`
  width: 1px;
  height: 2.2rem;
  background: ${C.border};
`;

const LicLogo = styled.img`
  height: 2.6rem;
  width: auto;
  border-radius: 0.3rem;
  object-fit: contain;
`;

const NavUser = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  color: ${C.textMuted};
  span {
    color: ${C.textPrimary};
    font-weight: 600;
  }
`;

// ─── Content Header ───────────────────────────────────────────────────────────
const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
  color: ${C.textSecondary};
  span { color: ${C.purple}; }
`;

const LiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${C.green};
  background: ${C.greenDim};
  border: 1px solid ${C.green}44;
  padding: 0.4rem 1.1rem;
  border-radius: 9999px;
  letter-spacing: 0.1em;
`;

const LiveDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${C.green};
  animation: ${pulseDot} 1.5s ease-in-out infinite;
`;

// ─── Stats ────────────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1.2rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-top: 3px solid ${(p) => p.accent || C.purple};
  border-radius: 1rem;
  padding: 1.6rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover {
    border-color: ${(p) => p.accent || C.purple}66;
    box-shadow: 0 4px 20px ${(p) => p.accent || C.purple}18;
  }
`;

const StatCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.4rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${C.textMuted};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const StatIconBox = styled.div`
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 0.8rem;
  background: ${(p) => p.color + '1e'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.color};
`;

const StatValue = styled.div`
  font-size: ${(p) => p.small ? '2rem' : '2.8rem'};
  font-weight: 800;
  color: ${(p) => p.color || C.textPrimary};
  line-height: 1;
  letter-spacing: -0.03em;
`;

// ─── Grid ─────────────────────────────────────────────────────────────────────
const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;
  align-items: stretch;
  flex: 1;
  min-height: 0;
`;

const Panel = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  padding: 1.1rem 1.6rem;
  border-bottom: 1px solid ${C.border};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${C.textMuted};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${C.bg};
`;

const CountPill = styled.span`
  background: ${(p) => (p.color || C.purple) + '1e'};
  color: ${(p) => p.color || C.purple};
  border: 1px solid ${(p) => (p.color || C.purple) + '44'};
  font-size: 1rem;
  font-weight: 700;
  padding: 0.2rem 0.8rem;
  border-radius: 9999px;
  letter-spacing: 0;
  text-transform: none;
`;

// ─── Doctor Roster ────────────────────────────────────────────────────────────
const DoctorScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const DoctorRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.4rem;
  border-bottom: 1px solid ${C.border};
  gap: 1rem;
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${C.border}44; }
`;

const DoctorAvatar = styled.div`
  width: 3.8rem;
  height: 3.8rem;
  border-radius: 50%;
  background: ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '18'};
  border: 2px solid ${(p) => STATUS_COLOR[p.status] || C.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${(p) => STATUS_COLOR[p.status] || C.textMuted};
  flex-shrink: 0;
`;

const DoctorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DoctorName = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${C.textPrimary};
  word-break: break-word;
`;

const DoctorMeta = styled.div`
  font-size: 1.05rem;
  color: ${C.textMuted};
`;

const DoctorTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
`;


const StatusToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.85rem 0.3rem 0.65rem;
  border-radius: 9999px;
  font-size: 0.95rem;
  font-weight: 600;
  border: 1.5px solid ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '55'};
  background: ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '18'};
  color: ${(p) => STATUS_COLOR[p.status] || C.textMuted};
  cursor: ${(p) => p.disabled ? 'default' : 'pointer'};
  transition: all 0.18s;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    background: ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '30'};
    border-color: ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '99'};
  }
`;

const ToggleDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => STATUS_COLOR[p.status] || C.textMuted};
  flex-shrink: 0;
`;

// ─── Queue Table ──────────────────────────────────────────────────────────────
const QueueScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const QueueTable = styled.div`width: 100%;`;

const Q_COLS = '2fr 1.4fr 1.1fr 1fr 1.3fr 8rem';

const QueueHead = styled.div`
  display: grid;
  grid-template-columns: ${Q_COLS};
  align-items: center;
  padding: 0.9rem 1.6rem;
  border-bottom: 2px solid ${C.border};
  font-size: 0.95rem;
  font-weight: 700;
  color: ${C.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  position: sticky;
  top: 0;
  background: ${C.bg};
  z-index: 5;
`;

const QueueRow = styled.div`
  display: grid;
  grid-template-columns: ${Q_COLS};
  align-items: center;
  padding: 1.05rem 1.6rem;
  border-bottom: 1px solid ${C.border};
  font-size: 1.2rem;
  background: ${(p) => p.urgent ? 'rgba(239,68,68,0.05)' : p.alt ? `${C.surfaceAlt}70` : 'transparent'};
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${C.borderLight}44 !important; }
`;

const CustomerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

const CustomerInitial = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${C.purpleDim};
  border: 1px solid ${C.purple}44;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${C.purple};
  flex-shrink: 0;
`;

const CustomerInfo = styled.div``;
const CustomerNameText = styled.div`font-weight: 600; color: ${C.textPrimary};`;
const CustomerGender = styled.div`font-size: 1rem; color: ${C.textMuted};`;

const AppIdText = styled.div`
  color: ${C.textMuted};
  font-size: 1.1rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  letter-spacing: 0.02em;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => STATUS_COLOR[p.status] || C.textMuted};
  background: ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '1a'};
  border: 1px solid ${(p) => (STATUS_COLOR[p.status] || C.textMuted) + '44'};
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  white-space: nowrap;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => STATUS_COLOR[p.status] || C.textMuted};
    flex-shrink: 0;
  }
`;

const SlaCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: ${(p) => p.urgent ? C.red : p.warning ? C.yellow : p.active ? C.green : C.textMuted};
`;

const AssignBtn = styled.button`
  background: ${C.purple};
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.45rem 1.1rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: ${C.purple}cc;
    box-shadow: 0 0 0 3px ${C.purpleDim};
  }
  &:disabled { opacity: 0.35; cursor: default; }
`;

const EmptyState = styled.div`
  padding: 5rem 2rem;
  text-align: center;
  color: ${C.textMuted};
`;

const EmptyIcon = styled.div`
  font-size: 3.2rem;
  margin-bottom: 1.2rem;
  color: ${C.border};
  display: flex;
  justify-content: center;
`;

const EmptyText = styled.div`font-size: 1.3rem;`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.6rem;
  border-top: 1px solid ${C.border};
  font-size: 1.1rem;
  color: ${C.textMuted};
  background: ${C.bg};
`;

const PageBtn = styled.button`
  background: transparent;
  border: 1px solid ${(p) => p.disabled ? C.border : C.borderLight};
  color: ${(p) => p.disabled ? C.border : C.textSecondary};
  border-radius: 0.4rem;
  padding: 0.35rem 0.9rem;
  font-size: 1.1rem;
  cursor: ${(p) => p.disabled ? 'default' : 'pointer'};
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: ${C.purple};
    color: ${C.purple};
  }
`;

// ─── Modal ────────────────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
`;

const ModalBox = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.borderLight};
  border-radius: 1.2rem;
  width: 40rem;
  max-width: 95vw;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.65);
  animation: ${slideIn} 0.22s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.8rem 2rem 1.5rem;
  border-bottom: 1px solid ${C.border};
`;

const ModalTitleBlock = styled.div``;

const ModalTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 0.3rem;
  color: ${C.textPrimary};
`;

const ModalSubtitle = styled.div`
  font-size: 1.15rem;
  color: ${C.textMuted};
  strong { color: ${C.textSecondary}; font-weight: 600; }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { border-color: ${C.red}; color: ${C.red}; background: ${C.redDim}; }
`;

const ModalBody = styled.div`
  padding: 1.6rem 2rem;
  max-height: 45vh;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.2rem 2rem 1.8rem;
  border-top: 1px solid ${C.border};
`;

const DoctorOption = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.2rem;
  border-radius: 0.8rem;
  cursor: pointer;
  margin-bottom: 0.6rem;
  background: ${(p) => p.selected ? C.purpleDim : 'transparent'};
  border: 1px solid ${(p) => p.selected ? C.purple : C.border};
  transition: all 0.15s;
  &:hover { border-color: ${C.purple}88; background: ${C.purpleDim}; }
`;

const SelectCircle = styled.div`
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  border: 2px solid ${(p) => p.selected ? C.purple : C.border};
  background: ${(p) => p.selected ? C.purple : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  &::after {
    content: '';
    display: ${(p) => p.selected ? 'block' : 'none'};
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: white;
  }
`;

const CancelBtn = styled.button`
  padding: 0.7rem 1.8rem;
  border-radius: 0.6rem;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${C.border};
  background: transparent;
  color: ${C.textSecondary};
  transition: all 0.15s;
  &:hover { border-color: ${C.borderLight}; background: ${C.border}55; }
`;

const ConfirmBtn = styled.button`
  padding: 0.7rem 1.8rem;
  border-radius: 0.6rem;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${C.purple};
  color: #fff;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${C.purple}cc;
    box-shadow: 0 0 0 3px ${C.purpleDim};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const HeaderBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${C.purple};
  color: #fff;
  border: none;
  border-radius: 0.6rem;
  padding: 0.55rem 1.2rem;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  margin-right: 1rem;
  &:hover { background: ${C.purple}cc; box-shadow: 0 0 0 3px ${C.purpleDim}; }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem 1.2rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  ${(p) => p.full ? 'grid-column: 1 / -1;' : ''}
`;

const FieldLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: ${C.textMuted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  span { color: ${C.red}; margin-left: 0.2rem; }
`;

const TextInput = styled.input`
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 0.5rem;
  padding: 0.7rem 0.9rem;
  font-size: 1.2rem;
  color: ${C.textPrimary};
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${C.purple}; }
  &::placeholder { color: ${C.textMuted}; }
`;

const Select = styled.select`
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 0.5rem;
  padding: 0.7rem 0.9rem;
  font-size: 1.2rem;
  color: ${C.textPrimary};
  outline: none;
  &:focus { border-color: ${C.purple}; }
`;

const FormError = styled.div`
  color: ${C.red};
  font-size: 1.1rem;
  margin-top: 0.4rem;
`;

const FormSuccess = styled.div`
  color: ${C.green};
  font-size: 1.1rem;
  margin-top: 0.4rem;
`;

// ─── Bulk-generate modal styled components ───────────────────────────────────
const Textarea = styled.textarea`
  width: 100%;
  min-height: 14rem;
  resize: vertical;
  padding: 1rem 1.1rem;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 0.5rem;
  color: ${C.textPrimary};
  font-size: 1.2rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  line-height: 1.55;
  &:focus {
    outline: none;
    border-color: ${C.purple};
    box-shadow: 0 0 0 2px ${C.purple}33;
  }
`;

const BulkHint = styled.div`
  font-size: 1.05rem;
  color: ${C.textMuted};
  margin-top: 0.5rem;
`;

const BulkSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.7rem;
  margin-bottom: 1.2rem;
`;

const BulkSummaryCard = styled.div`
  background: ${C.surface};
  border: 1px solid ${(p) => p.color ? p.color + '55' : C.border};
  border-top: 3px solid ${(p) => p.color || C.border};
  border-radius: 0.6rem;
  padding: 0.9rem 1rem;
`;

const BulkSummaryValue = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(p) => p.color || C.textPrimary};
  line-height: 1;
`;

const BulkSummaryLabel = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${C.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.4rem;
`;

const BulkResultsList = styled.div`
  max-height: 32vh;
  overflow-y: auto;
  border: 1px solid ${C.border};
  border-radius: 0.5rem;
`;

const BulkResultRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  align-items: center;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid ${C.border};
  font-size: 1.1rem;
  background: ${(p) =>
    p.status === 'success'    ? `${C.green}0d`  :
    p.status === 'not_found'  ? `${C.yellow}0d` :
    p.status === 'skipped'    ? `${C.yellow}0d` :
    p.status === 'error'      ? `${C.red}0d`    : 'transparent'};
  &:last-child { border-bottom: none; }
`;

const BulkRowAppId = styled.div`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  color: ${C.textSecondary};
  word-break: break-all;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const BulkRowUserHint = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.95rem;
  color: ${C.textMuted};
  font-weight: 400;
`;

const BulkRowStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) =>
    p.status === 'success'   ? C.green  :
    p.status === 'not_found' ? C.yellow :
    p.status === 'skipped'   ? C.yellow :
    p.status === 'error'     ? C.red    : C.textMuted};
  &::before {
    content: '';
    width: 6px; height: 6px; border-radius: 50%;
    background: currentColor;
  }
`;

const BulkRowMessage = styled.span`
  color: ${C.textMuted};
  font-weight: 500;
  margin-left: 0.4rem;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtSeconds = (s) => {
  if (!s && s !== 0) return '—';
  const h   = Math.floor(s / 3600);
  const m   = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const PAGE_SIZE = 50;

const EMPTY_REGISTER_FORM = {
  proposalNumber: '',
  name:          '',
  dob:           '',
  gender:        '',
  phone:         '',
  email:         '',
};

class LicOpsCenter extends Component {
  state = {
    assignModal:        null,
    selectedDoctor:     null,
    tick:               0,
    currentPage:        1,
    registerModalOpen:  false,
    registerForm:       { ...EMPTY_REGISTER_FORM },
    registering:        false,
    registerError:      '',
    registerSuccess:    '',
    bulkModalOpen:      false,
    bulkInput:          '',
    bulkProcessing:     false,
    bulkResults:        null,  // { summary: {...}, results: [...] } when set
    bulkError:          '',
  };

  componentDidMount() {
    this.props.loadOpsState();
    this.setupPubNub();
    this.pollTimer = setInterval(() => this.props.loadOpsState(), 10000);
    this.tickTimer = setInterval(() => this.setState((s) => ({ tick: s.tick + 1 })), 1000);
  }

  componentDidUpdate(prevProps) {
    const prevLen = (prevProps.queue || []).length;
    const currLen = (this.props.queue || []).length;
    if (prevLen !== currLen) {
      this.setState({ currentPage: 1 });
    }
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer);
    clearInterval(this.tickTimer);
    if (this.pubnub) this.pubnub.unsubscribeAll();
  }

  setupPubNub() {
    this.pubnub = new PubNub({
      publishKey:   config.pubnubPublishKey,
      subscribeKey: config.pubnubSubscribeKey,
      uuid:         `ops-${Date.now()}`,
    });
    this.pubnub.addListener({
      message: ({ message }) => {
        if (message.name === 'ops_queue_update') {
          this.props.applyOpsUpdate(message);
        }
      },
    });
    this.pubnub.subscribe({ channels: [OPS_CHANNEL] });
  }

  openAssignModal  = (consultationId, customerName) =>
    this.setState({ assignModal: { consultationId, customerName }, selectedDoctor: null });

  closeAssignModal = () => this.setState({ assignModal: null, selectedDoctor: null });

  confirmAssign = async () => {
    const { assignModal, selectedDoctor } = this.state;
    if (!assignModal || !selectedDoctor) return;
    await this.props.doManualAssign(assignModal.consultationId, selectedDoctor);
    this.closeAssignModal();
  };

  openRegisterModal = () =>
    this.setState({
      registerModalOpen: true,
      registerForm:      { ...EMPTY_REGISTER_FORM },
      registerError:     '',
      registerSuccess:   '',
    });

  closeRegisterModal = () => this.setState({
    registerModalOpen: false,
    registerError:     '',
    registerSuccess:   '',
  });

  updateRegisterField = (field) => (e) => {
    const value = e.target.value;
    this.setState((s) => ({
      registerForm: { ...s.registerForm, [field]: value },
    }));
  };

  openBulkModal = () => this.setState({
    bulkModalOpen: true,
    bulkInput:     '',
    bulkResults:   null,
    bulkError:     '',
  });

  closeBulkModal = () => this.setState({
    bulkModalOpen:  false,
    bulkInput:      '',
    bulkResults:    null,
    bulkError:      '',
    bulkProcessing: false,
  });

  updateBulkInput = (e) => this.setState({ bulkInput: e.target.value, bulkError: '' });

  submitBulkGenerate = async () => {
    // Split on newlines, commas, or semicolons so users can paste freely
    // from Excel/CSV/notes without reformatting.
    const raw = (this.state.bulkInput || '')
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (raw.length === 0) {
      this.setState({ bulkError: 'Please paste at least one application ID.' });
      return;
    }
    if (raw.length > 500) {
      this.setState({ bulkError: `Maximum 500 application IDs per batch. You pasted ${raw.length}.` });
      return;
    }

    this.setState({ bulkProcessing: true, bulkError: '', bulkResults: null });
    const data = await this.props.doBulkGenerateInstantLinks(raw);
    this.setState({ bulkProcessing: false });

    if (data && data.status === 'success') {
      this.setState({ bulkResults: data });
    } else {
      this.setState({ bulkError: (data && data.errorMessage) || 'Failed to process batch. Please try again.' });
    }
  };

  submitRegisterCase = async () => {
    const { registerForm } = this.state;
    const proposalNumber = (registerForm.proposalNumber || '').trim();
    const name          = (registerForm.name || '').trim();
    const phone         = (registerForm.phone || '').trim();

    if (!proposalNumber || !name || !phone) {
      this.setState({ registerError: 'Proposal Number, Name and Phone are required.', registerSuccess: '' });
      return;
    }
    if (!/^\d{10,}$/.test(phone)) {
      this.setState({ registerError: 'Phone must be at least 10 digits.', registerSuccess: '' });
      return;
    }

    this.setState({ registering: true, registerError: '', registerSuccess: '' });
    const payload = {
      proposalNumber,
      name,
      phone,
      dob:    (registerForm.dob || '').trim()    || undefined,
      gender: (registerForm.gender || '').trim() || undefined,
      email:  (registerForm.email || '').trim()  || undefined,
    };
    const data = await this.props.doRegisterInstantCase(payload);
    this.setState({ registering: false });

    if (data && data.status === 'success') {
      this.setState({ registerSuccess: 'Case registered. Link sent to customer.', registerError: '' });
      setTimeout(() => this.closeRegisterModal(), 1200);
    } else if (data && data.status === 'duplicate') {
      this.setState({ registerError: 'Case already registered for this Proposal Number.', registerSuccess: '' });
    } else {
      this.setState({
        registerError: (data && data.errorMessage) || 'Failed to register case. Please try again.',
        registerSuccess: '',
      });
    }
  };

  render() {
    const { stats, queue, doctors, doctorFirstName, doctorLastName, history } = this.props;
    const { assignModal, selectedDoctor, currentPage } = this.state;

    const waitingCount  = (queue   || []).filter((c) => c.status === 'WAITING').length;
    const inCallCount   = (queue   || []).filter((c) => ['ASSIGNED', 'IN_PROGRESS'].includes(c.status)).length;
    const availableCount = (doctors || []).filter((d) => d.status === 'AVAILABLE').length;

    const STATUS_TIER = { WAITING: 0, IN_PROGRESS: 1, ASSIGNED: 1, WRAP_UP: 2, COMPLETED: 3 };
    const sortedQueue = (queue || []).slice().sort((a, b) => {
      const tierA = STATUS_TIER[a.status] !== undefined ? STATUS_TIER[a.status] : 99;
      const tierB = STATUS_TIER[b.status] !== undefined ? STATUS_TIER[b.status] : 99;
      if (tierA !== tierB) return tierA - tierB;
      return (b.elapsedSeconds || 0) - (a.elapsedSeconds || 0);
    });

    const totalPages = Math.max(1, Math.ceil(sortedQueue.length / PAGE_SIZE));
    const pagedQueue = sortedQueue.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const availableDoctors = (doctors || [])
      .filter((d) => d.status === 'AVAILABLE')
      .sort((a, b) => (a.callsToday || 0) - (b.callsToday || 0));

    return (
      <Page>
        {/* ── Nav ── */}
        <TopNav>
          <BackBtn onClick={() => history.push('/home')}>
            <FiArrowLeft size={15} /> Back
          </BackBtn>
          <BrandArea>
            <VisitLogo src={visitLogo} alt="Visit Health" />
            <LogoDivider />
            <LicLogo
              src="https://visit-public.s3.ap-south-1.amazonaws.com/assets/lic_logo.jpg"
              alt="LIC"
            />
          </BrandArea>
          {(doctorFirstName || doctorLastName) && (
            <NavUser>
              <FiUser size={13} />
              Dr. <span>{doctorFirstName} {doctorLastName}</span>
            </NavUser>
          )}
        </TopNav>

        {/* ── Page Title ── */}
        <ContentHeader>
          <PageTitle>LIC Instant <span>vMER</span> Ops Center</PageTitle>
          <HeaderActions>
            <HeaderBtn onClick={this.openRegisterModal}>
              <FiPlus size={14} /> Register Instant Case
            </HeaderBtn>
            <HeaderBtn onClick={this.openBulkModal}>
              <FiUpload size={14} /> Bulk Generate Links
            </HeaderBtn>
            <LiveBadge><LiveDot /> LIVE</LiveBadge>
          </HeaderActions>
        </ContentHeader>

        {/* ── Stats ── */}
        <StatsRow>
          <StatCard accent={C.purple}>
            <StatCardTop>
              <StatLabel>Cases Today</StatLabel>
              <StatIconBox color={C.purple}><FiBarChart2 size={17} /></StatIconBox>
            </StatCardTop>
            <StatValue>{stats.totalToday || 0}</StatValue>
          </StatCard>

          <StatCard accent={C.yellow}>
            <StatCardTop>
              <StatLabel>Waiting</StatLabel>
              <StatIconBox color={C.yellow}><FiClock size={17} /></StatIconBox>
            </StatCardTop>
            <StatValue color={waitingCount > 0 ? C.yellow : undefined}>{waitingCount}</StatValue>
          </StatCard>

          <StatCard accent={C.green}>
            <StatCardTop>
              <StatLabel>In Call</StatLabel>
              <StatIconBox color={C.green}><FiPhone size={17} /></StatIconBox>
            </StatCardTop>
            <StatValue color={C.green}>{inCallCount}</StatValue>
          </StatCard>

          <StatCard accent={C.textMuted}>
            <StatCardTop>
              <StatLabel>Completed</StatLabel>
              <StatIconBox color={C.textMuted}><FiCheckCircle size={17} /></StatIconBox>
            </StatCardTop>
            <StatValue>{stats.completed || 0}</StatValue>
          </StatCard>

          <StatCard accent={C.blue}>
            <StatCardTop>
              <StatLabel>Avg Wait</StatLabel>
              <StatIconBox color={C.blue}><FiActivity size={17} /></StatIconBox>
            </StatCardTop>
            <StatValue color={C.blue} small={!!stats.avgWaitSeconds}>
              {stats.avgWaitSeconds ? fmtSeconds(stats.avgWaitSeconds) : '—'}
            </StatValue>
          </StatCard>
        </StatsRow>

        <Grid>
          {/* ── Doctor Roster ── */}
          <Panel>
            <PanelHeader>
              Doctor Roster
              <CountPill color={C.green}>{availableCount} available</CountPill>
            </PanelHeader>
            {(doctors || []).length === 0 ? (
              <EmptyState>
                <EmptyIcon><FiUsers size={36} /></EmptyIcon>
                <EmptyText>No doctors found</EmptyText>
              </EmptyState>
            ) : (
              <DoctorScrollArea>
                {(doctors || []).map((d) => (
                  <DoctorRow key={d.doctorId}>
                    <DoctorAvatar status={d.status}>
                      {(d.doctorName || '?')[0].toUpperCase()}
                    </DoctorAvatar>
                    <DoctorInfo>
                      <DoctorTopRow>
                        <DoctorName>Dr. {d.doctorName}</DoctorName>
                        <StatusToggle
                          status={d.status}
                          disabled={d.status === 'IN_CALL'}
                          title={
                            d.status === 'IN_CALL'    ? 'Doctor is in a call' :
                            d.status === 'AVAILABLE'  ? 'Click to set Offline' :
                                                        'Click to set Available'
                          }
                          onClick={() => {
                            if (d.status === 'AVAILABLE') this.props.setOpsDoctorStatus(d.doctorId, 'OFFLINE');
                            else if (d.status === 'OFFLINE') this.props.setOpsDoctorStatus(d.doctorId, 'AVAILABLE');
                          }}
                        >
                          <ToggleDot status={d.status} />
                          {STATUS_LABEL[d.status] || d.status}
                        </StatusToggle>
                      </DoctorTopRow>
                      <DoctorMeta>{d.callsToday || 0} calls today</DoctorMeta>
                    </DoctorInfo>
                  </DoctorRow>
                ))}
              </DoctorScrollArea>
            )}
          </Panel>

          {/* ── Live Queue ── */}
          <Panel>
            <PanelHeader>
              Live Queue
              <CountPill color={waitingCount > 0 ? C.yellow : C.textMuted}>
                {waitingCount} waiting
              </CountPill>
            </PanelHeader>

            {(queue || []).length === 0 ? (
              <EmptyState>
                <EmptyIcon><FiActivity size={36} /></EmptyIcon>
                <EmptyText>No cases today yet</EmptyText>
              </EmptyState>
            ) : (
              <QueueScrollArea>
                <QueueTable>
                  <QueueHead>
                    <span>Customer</span>
                    <span>Proposal Number</span>
                    <span>Status</span>
                    <span>Wait / Elapsed</span>
                    <span>Doctor</span>
                    <span>Action</span>
                  </QueueHead>
                  {pagedQueue.map((c, i) => {
                    const elapsed = c.queuedAt
                      ? Math.floor((Date.now() - new Date(c.queuedAt).getTime()) / 1000)
                      : (c.elapsedSeconds || 0);
                    const urgent  = c.status === 'WAITING' && elapsed > 180;
                    const warning = c.status === 'WAITING' && elapsed > 90;
                    return (
                      <QueueRow key={c.consultationId} urgent={urgent} alt={i % 2 === 1}>
                        <CustomerCell>
                          <CustomerInitial>{(c.name || '?')[0].toUpperCase()}</CustomerInitial>
                          <CustomerInfo>
                            <CustomerNameText>{c.name}</CustomerNameText>
                            <CustomerGender>{c.gender || '—'}</CustomerGender>
                          </CustomerInfo>
                        </CustomerCell>
                        <AppIdText>{c.proposalNumber || '—'}</AppIdText>
                        <div><StatusBadge status={c.status}>{STATUS_LABEL[c.status] || c.status}</StatusBadge></div>
                        <SlaCell urgent={urgent} warning={warning} active={c.status === 'WAITING'}>
                          {urgent && <FiAlertTriangle size={13} />}
                          {fmtSeconds(elapsed)}
                        </SlaCell>
                        <div style={{ fontSize: '1.1rem', color: C.textSecondary }}>
                          {c.doctorName ? `Dr. ${c.doctorName}` : '—'}
                        </div>
                        <div>
                          {c.status === 'WAITING' && (
                            <AssignBtn
                              disabled={availableDoctors.length === 0}
                              onClick={() => this.openAssignModal(c.consultationId, c.name)}
                            >
                              Assign
                            </AssignBtn>
                          )}
                        </div>
                      </QueueRow>
                    );
                  })}
                </QueueTable>
              </QueueScrollArea>
            )}

            {sortedQueue.length > PAGE_SIZE && (
              <PaginationBar>
                <span>{sortedQueue.length} cases &nbsp;·&nbsp; Page {currentPage} of {totalPages}</span>
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  <PageBtn disabled={currentPage === 1} onClick={() => this.setState({ currentPage: 1 })}>«</PageBtn>
                  <PageBtn disabled={currentPage === 1} onClick={() => this.setState((s) => ({ currentPage: s.currentPage - 1 }))}>‹ Prev</PageBtn>
                  <PageBtn disabled={currentPage === totalPages} onClick={() => this.setState((s) => ({ currentPage: s.currentPage + 1 }))}>Next ›</PageBtn>
                  <PageBtn disabled={currentPage === totalPages} onClick={() => this.setState({ currentPage: totalPages })}>»</PageBtn>
                </div>
              </PaginationBar>
            )}
          </Panel>
        </Grid>

        {/* ── Manual Assign Modal ── */}
        {assignModal && (
          <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) this.closeAssignModal(); }}>
            <ModalBox>
              <ModalHeader>
                <ModalTitleBlock>
                  <ModalTitle>Assign Doctor</ModalTitle>
                  <ModalSubtitle>
                    Customer: <strong>{assignModal.customerName}</strong>
                  </ModalSubtitle>
                </ModalTitleBlock>
                <CloseBtn onClick={this.closeAssignModal}><FiX size={15} /></CloseBtn>
              </ModalHeader>

              <ModalBody>
                {availableDoctors.length === 0 ? (
                  <div style={{ color: C.yellow, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                    <FiAlertTriangle size={16} />
                    No available doctors at the moment.
                  </div>
                ) : (
                  availableDoctors.map((d) => (
                    <DoctorOption
                      key={d.doctorId}
                      selected={selectedDoctor === d.doctorId}
                      onClick={() => this.setState({ selectedDoctor: d.doctorId })}
                    >
                      <SelectCircle selected={selectedDoctor === d.doctorId} />
                      <DoctorAvatar status={d.status} style={{ width: '3.4rem', height: '3.4rem', fontSize: '1.3rem' }}>
                        {(d.doctorName || '?')[0].toUpperCase()}
                      </DoctorAvatar>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '1.3rem' }}>Dr. {d.doctorName}</div>
                        <div style={{ fontSize: '1.1rem', color: C.textMuted }}>{d.callsToday || 0} calls today</div>
                      </div>
                    </DoctorOption>
                  ))
                )}
              </ModalBody>

              <ModalFooter>
                <CancelBtn onClick={this.closeAssignModal}>Cancel</CancelBtn>
                <ConfirmBtn disabled={!selectedDoctor} onClick={this.confirmAssign}>
                  Confirm Assign
                </ConfirmBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* ── Register Instant Case Modal ── */}
        {this.state.registerModalOpen && (
          <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) this.closeRegisterModal(); }}>
            <ModalBox>
              <ModalHeader>
                <ModalTitleBlock>
                  <ModalTitle>Register Instant Case</ModalTitle>
                  <ModalSubtitle>The customer will receive a video-call link via SMS &amp; WhatsApp.</ModalSubtitle>
                </ModalTitleBlock>
                <CloseBtn onClick={this.closeRegisterModal}><FiX size={15} /></CloseBtn>
              </ModalHeader>

              <ModalBody>
                <FormGrid>
                  <Field>
                    <FieldLabel htmlFor="reg-app-id">Proposal Number<span>*</span></FieldLabel>
                    <TextInput
                      id="reg-app-id"
                      value={this.state.registerForm.proposalNumber}
                      onChange={this.updateRegisterField('proposalNumber')}
                      placeholder="Proposal Number"
                      autoComplete="off"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="reg-name">Name<span>*</span></FieldLabel>
                    <TextInput
                      id="reg-name"
                      value={this.state.registerForm.name}
                      onChange={this.updateRegisterField('name')}
                      placeholder="Customer name"
                      autoComplete="off"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="reg-phone">Phone<span>*</span></FieldLabel>
                    <TextInput
                      id="reg-phone"
                      value={this.state.registerForm.phone}
                      onChange={this.updateRegisterField('phone')}
                      placeholder="10-digit mobile"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="reg-dob">Date of Birth</FieldLabel>
                    <TextInput
                      id="reg-dob"
                      type="date"
                      value={this.state.registerForm.dob}
                      onChange={this.updateRegisterField('dob')}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="reg-gender">Gender</FieldLabel>
                    <Select
                      id="reg-gender"
                      value={this.state.registerForm.gender}
                      onChange={this.updateRegisterField('gender')}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Select>
                  </Field>
                  <Field full>
                    <FieldLabel htmlFor="reg-email">Email</FieldLabel>
                    <TextInput
                      id="reg-email"
                      type="email"
                      value={this.state.registerForm.email}
                      onChange={this.updateRegisterField('email')}
                      placeholder="customer@example.com"
                      autoComplete="off"
                    />
                  </Field>
                </FormGrid>

                {this.state.registerError && <FormError>{this.state.registerError}</FormError>}
                {this.state.registerSuccess && <FormSuccess>{this.state.registerSuccess}</FormSuccess>}
              </ModalBody>

              <ModalFooter>
                <CancelBtn onClick={this.closeRegisterModal} disabled={this.state.registering}>Cancel</CancelBtn>
                <ConfirmBtn onClick={this.submitRegisterCase} disabled={this.state.registering}>
                  {this.state.registering ? 'Registering…' : 'Register & Send Link'}
                </ConfirmBtn>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {this.state.bulkModalOpen && (
          <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget && !this.state.bulkProcessing) this.closeBulkModal(); }}>
            <ModalBox>
              <ModalHeader>
                <ModalTitleBlock>
                  <ModalTitle>Bulk Generate Instant vMER Links</ModalTitle>
                  <ModalSubtitle>
                    Paste application IDs (one per line). Each will be looked up in <code>lic_users</code>, a fresh link generated, and an SMS sent to the customer.
                  </ModalSubtitle>
                </ModalTitleBlock>
                <CloseBtn onClick={this.closeBulkModal} disabled={this.state.bulkProcessing}><FiX size={15} /></CloseBtn>
              </ModalHeader>

              <ModalBody>
                {!this.state.bulkResults && (
                  <>
                    <Field full>
                      <FieldLabel htmlFor="bulk-input">Proposal Numbers<span>*</span></FieldLabel>
                      <Textarea
                        id="bulk-input"
                        value={this.state.bulkInput}
                        onChange={this.updateBulkInput}
                        placeholder={'LIC-001\nLIC-002\nLIC-003\n…'}
                        disabled={this.state.bulkProcessing}
                      />
                      <BulkHint>Newlines, commas or spaces all work. Max 500 per batch.</BulkHint>
                    </Field>
                    {this.state.bulkError && <FormError>{this.state.bulkError}</FormError>}
                  </>
                )}

                {this.state.bulkResults && (
                  <>
                    <BulkSummary>
                      <BulkSummaryCard color={C.purple}>
                        <BulkSummaryValue>{this.state.bulkResults.summary.total}</BulkSummaryValue>
                        <BulkSummaryLabel>Total Rows</BulkSummaryLabel>
                      </BulkSummaryCard>
                      <BulkSummaryCard color={C.green}>
                        <BulkSummaryValue color={C.green}>{this.state.bulkResults.summary.successful}</BulkSummaryValue>
                        <BulkSummaryLabel>Successful</BulkSummaryLabel>
                      </BulkSummaryCard>
                      <BulkSummaryCard color={C.yellow}>
                        <BulkSummaryValue color={C.yellow}>{this.state.bulkResults.summary.skipped || 0}</BulkSummaryValue>
                        <BulkSummaryLabel>Skipped</BulkSummaryLabel>
                      </BulkSummaryCard>
                      <BulkSummaryCard color={C.yellow}>
                        <BulkSummaryValue color={C.yellow}>{this.state.bulkResults.summary.notFound}</BulkSummaryValue>
                        <BulkSummaryLabel>Not Found</BulkSummaryLabel>
                      </BulkSummaryCard>
                      <BulkSummaryCard color={C.red}>
                        <BulkSummaryValue color={C.red}>{this.state.bulkResults.summary.errors}</BulkSummaryValue>
                        <BulkSummaryLabel>Errors</BulkSummaryLabel>
                      </BulkSummaryCard>
                    </BulkSummary>

                    {this.state.bulkResults.summary.proposalsSubmitted != null && (
                      <BulkHint style={{ marginBottom: '0.4rem' }}>
                        Processed <strong>{this.state.bulkResults.summary.proposalsSubmitted}</strong> proposal{this.state.bulkResults.summary.proposalsSubmitted === 1 ? '' : 's'} → <strong>{this.state.bulkResults.summary.total}</strong> user row{this.state.bulkResults.summary.total === 1 ? '' : 's'} (LIC can have multiple users per proposal).
                      </BulkHint>
                    )}

                    {this.state.bulkResults.summary.duplicatesInBatch > 0 && (
                      <BulkHint style={{ marginBottom: '0.8rem' }}>
                        Removed <strong>{this.state.bulkResults.summary.duplicatesInBatch}</strong> duplicate proposal ID{this.state.bulkResults.summary.duplicatesInBatch === 1 ? '' : 's'} from the input.
                      </BulkHint>
                    )}

                    <BulkResultsList>
                      {this.state.bulkResults.results.map((row, i) => (
                        <BulkResultRow key={`${row.applicationId}-${row.userId || 'na'}-${i}`} status={row.status}>
                          <BulkRowAppId>
                            <span>{row.applicationId}</span>
                            {(row.customerName || row.userId) && (
                              <BulkRowUserHint>
                                {row.customerName ? row.customerName : ''}
                                {row.customerName && row.userId ? ' · ' : ''}
                                {row.userId ? `user #${row.userId}` : ''}
                              </BulkRowUserHint>
                            )}
                          </BulkRowAppId>
                          <div>
                            <BulkRowStatus status={row.status}>
                              {row.status === 'success'   && 'Link sent'}
                              {row.status === 'skipped'   && 'Skipped'}
                              {row.status === 'not_found' && 'Not found'}
                              {row.status === 'error'     && 'Error'}
                            </BulkRowStatus>
                            {row.message && <BulkRowMessage>· {row.message}</BulkRowMessage>}
                          </div>
                        </BulkResultRow>
                      ))}
                    </BulkResultsList>
                  </>
                )}
              </ModalBody>

              <ModalFooter>
                {!this.state.bulkResults ? (
                  <>
                    <CancelBtn onClick={this.closeBulkModal} disabled={this.state.bulkProcessing}>Cancel</CancelBtn>
                    <ConfirmBtn onClick={this.submitBulkGenerate} disabled={this.state.bulkProcessing}>
                      {this.state.bulkProcessing ? 'Processing…' : 'Generate & Send Links'}
                    </ConfirmBtn>
                  </>
                ) : (
                  <ConfirmBtn onClick={this.closeBulkModal}>Done</ConfirmBtn>
                )}
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </Page>
    );
  }
}

const mapStateToProps = (state) => ({
  stats:           state.licQueue.stats,
  queue:           state.licQueue.queue,
  doctors:         state.licQueue.doctors,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
});

const mapDispatchToProps = {
  loadOpsState, applyOpsUpdate, doManualAssign, setOpsDoctorStatus,
  doRegisterInstantCase, doBulkGenerateInstantLinks,
};

export default connect(mapStateToProps, mapDispatchToProps)(LicOpsCenter);
